import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

type DimensionHeaderTooltipProps = {
  labels: string[];
};

type MetricHeaderTooltipProps = {
  label: string;
  tip: string;
};

const tooltipViewportPadding = 8;
const tooltipGap = 9;
const tooltipWidth = 360;

type TooltipPosition = {
  left: number;
  maxHeight: number;
  top?: number;
  bottom?: number;
};

/**
 * Shows the metric definition only while the corresponding table heading is
 * hovered, so dense report tables do not render every definition on load.
 */
export function MetricHeaderTooltip({ label, tip }: MetricHeaderTooltipProps) {
  const triggerRef = useRef<HTMLSpanElement>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [position, setPosition] = useState<TooltipPosition | null>(null);

  const updatePosition = useCallback(() => {
    const trigger = triggerRef.current;
    if (!trigger) return;

    const rect = trigger.getBoundingClientRect();
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    const panelWidth = Math.min(tooltipWidth, viewportWidth - tooltipViewportPadding * 2);
    const left = Math.min(
      Math.max(rect.left + rect.width / 2, tooltipViewportPadding + panelWidth / 2),
      viewportWidth - tooltipViewportPadding - panelWidth / 2,
    );
    const spaceBelow = viewportHeight - rect.bottom - tooltipGap - tooltipViewportPadding;
    const spaceAbove = rect.top - tooltipGap - tooltipViewportPadding;

    if (spaceBelow < 150 && spaceAbove > spaceBelow) {
      setPosition({ left, bottom: viewportHeight - rect.top + tooltipGap, maxHeight: Math.max(0, spaceAbove) });
      return;
    }
    setPosition({ left, top: rect.bottom + tooltipGap, maxHeight: Math.max(0, spaceBelow) });
  }, []);

  useEffect(() => {
    if (!isOpen) return;
    updatePosition();
    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition, true);
    return () => {
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition, true);
    };
  }, [isOpen, updatePosition]);

  const openTooltip = useCallback(() => {
    updatePosition();
    setIsOpen(true);
  }, [updatePosition]);

  return (
    <span
      ref={triggerRef}
      className="metric-header-tooltip"
      onMouseEnter={openTooltip}
      onMouseLeave={() => setIsOpen(false)}
    >
      <span>{label}</span>
      <span className="metric-header-tooltip-icon" aria-hidden="true">?</span>
      {isOpen && position && createPortal(
        <span
          className="metric-header-tooltip-panel"
          role="tooltip"
          style={{ left: position.left, top: position.top, bottom: position.bottom, maxHeight: position.maxHeight }}
        >
          <span className="metric-header-tooltip-title">{label}</span>
          <span className="metric-header-tooltip-row">
            <span className="metric-header-tooltip-label">口径描述</span>
            <span className="metric-header-tooltip-content">{tip}</span>
          </span>
        </span>,
        document.body,
      )}
    </span>
  );
}

export function DimensionHeaderTooltip({ labels }: DimensionHeaderTooltipProps) {
  const summary = `下钻维度（可选：${labels.join('、')}）`;
  const summaryRef = useRef<HTMLSpanElement>(null);
  const [isTruncated, setIsTruncated] = useState(false);

  const measureTruncation = () => {
    const element = summaryRef.current;
    setIsTruncated(Boolean(element && element.scrollWidth > element.clientWidth));
  };

  return (
    <span className="tree-dimension-tooltip" onMouseEnter={measureTruncation}>
      <span ref={summaryRef} className="tree-dimension-header">{summary}</span>
      {isTruncated && <span className="tree-dimension-tooltip-panel" role="tooltip">
        <span className="tree-dimension-tooltip-title">可下钻维度</span>
        <span className="tree-dimension-tooltip-labels">
          {labels.map(label => <span key={label} className="tree-dimension-tooltip-label">{label}</span>)}
        </span>
      </span>}
    </span>
  );
}

export function TruncatedValueTooltip({ value }: { value: string }) {
  const valueRef = useRef<HTMLSpanElement>(null);
  const [isTruncated, setIsTruncated] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [position, setPosition] = useState<TooltipPosition | null>(null);

  const updatePosition = useCallback(() => {
    const element = valueRef.current;
    if (!element) return;
    const rect = element.getBoundingClientRect();
    const panelWidth = Math.min(tooltipWidth, window.innerWidth - tooltipViewportPadding * 2);
    const left = Math.min(Math.max(rect.left, tooltipViewportPadding), window.innerWidth - tooltipViewportPadding - panelWidth);
    const spaceBelow = window.innerHeight - rect.bottom - tooltipGap - tooltipViewportPadding;
    const spaceAbove = rect.top - tooltipGap - tooltipViewportPadding;
    if (spaceBelow < 90 && spaceAbove > spaceBelow) {
      setPosition({ left, bottom: window.innerHeight - rect.top + tooltipGap, maxHeight: Math.max(0, spaceAbove) });
      return;
    }
    setPosition({ left, top: rect.bottom + tooltipGap, maxHeight: Math.max(0, spaceBelow) });
  }, []);

  useEffect(() => {
    if (!isOpen) return;
    updatePosition();
    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition, true);
    return () => {
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition, true);
    };
  }, [isOpen, updatePosition]);

  const openTooltip = () => {
    const element = valueRef.current;
    const truncated = Boolean(element && element.scrollWidth > element.clientWidth);
    setIsTruncated(truncated);
    if (!truncated) return;
    updatePosition();
    setIsOpen(true);
  };

  return (
    <span className={`tree-value-tooltip${isTruncated ? ' has-tooltip' : ''}`} onMouseEnter={openTooltip} onMouseLeave={() => setIsOpen(false)}>
      <span ref={valueRef} className="tree-cell-value" title={value}>{value}</span>
      {isOpen && isTruncated && position && createPortal(
        <span className="tree-value-tooltip-panel" role="tooltip" style={{ left: position.left, top: position.top, bottom: position.bottom, maxHeight: position.maxHeight }}>
          {value}
        </span>,
        document.body,
      )}
    </span>
  );
}
